import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getTeam, inviteMember, revokeInvitation, removeMember } from "../lib/api.ts";

interface TeamMember {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: "owner" | "admin" | "staff";
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  invited_at: string;
  expires_at: string;
}

interface TeamData {
  members: TeamMember[];
  invitations: Invitation[];
}

const ROLES = ["admin", "staff"] as const;
type Role = (typeof ROLES)[number];

function initials(s: string | null | undefined) {
  if (!s) return "?";
  const parts = s.trim().split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function roleBadge(role: string) {
  // Owner gets ink-on-canvas (the one truly distinct role).
  // Admin and Staff are visually equivalent — different role, same status surface.
  if (role === "owner") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          background: "var(--ink)",
          color: "var(--canvas)",
          borderRadius: 999,
          padding: "2px 10px",
          fontSize: 11.5,
          fontWeight: 500,
          letterSpacing: "0.02em",
        }}
      >
        Owner
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: "var(--paper-well)",
        color: "var(--ink-soft)",
        borderRadius: 999,
        padding: "2px 10px",
        fontSize: 11.5,
        fontWeight: 500,
        textTransform: "capitalize",
      }}
    >
      {role}
    </span>
  );
}

export default function Team() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<TeamData>({ members: [], invitations: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("staff");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    getTeam(slug)
      .then((d) => setData(d as TeamData))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug) return;
    setInviting(true);
    setInviteError(null);
    setInviteSuccess(false);
    try {
      const inv = (await inviteMember(slug, { email: inviteEmail, role: inviteRole })) as Invitation;
      setData((d) => ({ ...d, invitations: [...d.invitations, inv] }));
      setInviteEmail("");
      setInviteSuccess(true);
      setTimeout(() => setInviteSuccess(false), 3000);
    } catch (err) {
      setInviteError((err as Error).message);
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (inviteId: string) => {
    if (!slug || !confirm("Revoke this invitation?")) return;
    setActionLoading(inviteId);
    try {
      await revokeInvitation(slug, inviteId);
      setData((d) => ({ ...d, invitations: d.invitations.filter((i) => i.id !== inviteId) }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (memberId: string) => {
    if (!slug || !confirm("Remove this team member?")) return;
    setActionLoading(memberId);
    try {
      await removeMember(slug, memberId);
      setData((d) => ({ ...d, members: d.members.filter((m) => m.id !== memberId) }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionLoading(null);
    }
  };

  const total = data.members.length;

  return (
    <div>
      <section className="zd-hero" style={{ marginBottom: 32 }}>
        <div className="zd-hero-text">
          <div className="zd-hero-kicker">
            Access <span className="dot">·</span> {total} {total === 1 ? "member" : "members"}
            {data.invitations.length > 0 && <> <span className="dot">·</span> {data.invitations.length} pending</>}
          </div>
          <h1 className="zd-hero-headline">Team</h1>
          <p className="zd-hero-lead">Who can sign in and what they're allowed to do. Owners can do everything; admins manage day-to-day; staff see their own schedule.</p>
        </div>
      </section>

      {error && <div className="alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Invite form */}
      <section
        style={{
          background: "var(--paper-raised)",
          borderRadius: 14,
          padding: 22,
          boxShadow: "var(--shadow-sm)",
          marginBottom: 24,
        }}
      >
        <h2
          style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontSize: 20,
            fontWeight: 500,
            letterSpacing: "-0.012em",
            color: "var(--ink)",
            marginBottom: 4,
          }}
        >
          Invite someone
        </h2>
        <p style={{ fontSize: 13, color: "var(--ink-soft)", marginBottom: 16 }}>
          We'll email them a link to join. Invites expire after 7 days.
        </p>

        {inviteError && <div className="alert-error" style={{ marginBottom: 12 }}>{inviteError}</div>}
        {inviteSuccess && <div className="alert-success" style={{ marginBottom: 12 }}>Invitation sent.</div>}

        <form onSubmit={handleInvite} className="flex flex-wrap gap-3 items-end">
          <div style={{ flex: 1, minWidth: 220 }}>
            <label className="label">Email</label>
            <input
              required
              type="email"
              className="input"
              placeholder="colleague@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Role</label>
            <select
              className="input"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as Role)}
              style={{ minWidth: 140, textTransform: "capitalize" }}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <button type="submit" disabled={inviting} className="btn-primary">
            {inviting ? "Sending…" : "Send invite"}
          </button>
        </form>
      </section>

      {loading ? (
        <div style={{ padding: "48px 0", textAlign: "center", color: "var(--ink-faint)", fontSize: 14 }}>
          Loading…
        </div>
      ) : (
        <>
          {/* Members */}
          <div className="zd-section" style={{ marginTop: 0 }}>
            <h2>Members</h2>
            <div className="right">
              <b>{data.members.length}</b> {data.members.length === 1 ? "person" : "people"}
            </div>
          </div>

          {data.members.length === 0 ? (
            <div className="zd-empty">
              <h3>Just you so far.</h3>
              <p>Invite the people who need to manage appointments, services, or staff.</p>
            </div>
          ) : (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {data.members.map((m) => (
                <article
                  key={m.id}
                  style={{
                    background: "var(--paper-raised)",
                    borderRadius: 14,
                    padding: 16,
                    boxShadow: "var(--shadow-sm)",
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                  }}
                >
                  <span
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "var(--accent-soft)",
                      color: "var(--accent-ink)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12.5,
                      fontWeight: 600,
                      border: "1px solid var(--rule)",
                      flexShrink: 0,
                    }}
                  >
                    {initials(m.name || m.email)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>
                        {m.name || m.email}
                      </span>
                      {roleBadge(m.role)}
                    </div>
                    {m.name && (
                      <div style={{ fontSize: 12.5, color: "var(--ink-soft)", marginTop: 2 }}>{m.email}</div>
                    )}
                  </div>
                  {m.role !== "owner" && (
                    <button
                      onClick={() => handleRemove(m.id)}
                      disabled={actionLoading === m.id}
                      className="zd-ghost-btn"
                      style={{ color: "var(--status-cancelled)" }}
                    >
                      {actionLoading === m.id ? "…" : "Remove"}
                    </button>
                  )}
                </article>
              ))}
            </div>
          )}

          {/* Pending invitations */}
          {data.invitations.length > 0 && (
            <>
              <div className="zd-section">
                <h2>Pending invitations</h2>
                <div className="right">
                  <b>{data.invitations.length}</b> waiting
                </div>
              </div>
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {data.invitations.map((inv) => (
                  <article
                    key={inv.id}
                    style={{
                      background: "var(--paper-raised)",
                      borderRadius: 14,
                      padding: 16,
                      boxShadow: "var(--shadow-sm)",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                    }}
                  >
                    <span
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: "var(--paper-well)",
                        color: "var(--ink-soft)",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12.5,
                        fontWeight: 600,
                        border: "1px solid var(--rule)",
                        flexShrink: 0,
                      }}
                    >
                      {initials(inv.email)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>{inv.email}</span>
                        {roleBadge(inv.role)}
                      </div>
                      <div style={{ fontSize: 12.5, color: "var(--ink-faint)", marginTop: 2 }}>
                        Invited {new Date(inv.invited_at).toLocaleDateString()} · expires{" "}
                        {new Date(inv.expires_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevoke(inv.id)}
                      disabled={actionLoading === inv.id}
                      className="zd-ghost-btn"
                    >
                      {actionLoading === inv.id ? "…" : "Revoke"}
                    </button>
                  </article>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
